import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { MdCheckCircle, MdCancel, MdContentCopy } from "react-icons/md";
import toast from "react-hot-toast";
import moment from "moment";

const money = (v) => Number(v || 0).toFixed(2);
const dt = (v) => (v ? moment(v).format("DD MMM YYYY, hh:mm A") : "—");

const Row = ({ label, children, strong }) => (
  <div className="wdd-row">
    <span className="wdd-label">{label}</span>
    <span className={`wdd-value ${strong ? "wdd-value-strong" : ""}`}>
      {children}
    </span>
  </div>
);

/**
 * Read-only detail view. Approve/reject route through WithdrawalActionModal so
 * the confirmation step is never skipped.
 */
const WithdrawalDetailsModal = ({ row, onClose, onAct }) => {
  if (!row) return null;

  const hasFee = Number(row.fee_amount) > 0;
  const isPending = row.status === "PENDING";
  const isApproved = row.status === "APPROVED";

  const copy = (text, label) => {
    navigator.clipboard
      .writeText(String(text || ""))
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error("Could not copy"));
  };

  return (
    <Modal show onHide={onClose} backdrop="static" centered size="lg">
      <div className="modal-header border-0 pb-0">
        <h5 className="modal-title">Withdrawal Details</h5>
        <button type="button" className="btn" onClick={onClose}>
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />

      <Modal.Body className="wdd-body">
        <div className="wdd-hero">
          <div>
            <div className="wdd-hero-label">Payout to Player</div>
            <div className="wdd-hero-amount">₹{money(row.payout_amount)}</div>
            {hasFee && (
              <div className="wdd-hero-break">
                ₹{money(row.amount)} − ₹{money(row.fee_amount)} fee (
                {Number(row.fee_percentage)}%)
              </div>
            )}
          </div>
          <span className={`wd-badge wd-badge-${String(row.status).toLowerCase()}`}>
            {row.status}
          </span>
        </div>

        <div className="wdd-grid">
          <section className="wdd-section">
            <h6 className="wdd-section-title">Player</h6>
            <Row label="Name">{row.user_name}</Row>
            <Row label="Game ID">{row.game_id || "—"}</Row>
            <Row label="Mobile">{row.mobile || "—"}</Row>
            <Row label="Parent Account">{row.parent_name || "—"}</Row>
          </section>

          {/* The figures the admin needs to actually make the transfer. */}
          <section className="wdd-section wdd-section-bank">
            <h6 className="wdd-section-title">Payout Destination</h6>
            <Row label="Bank">{row.bank_name || "—"}</Row>
            <Row label="Holder">{row.account_holder_name || "—"}</Row>
            <Row label="Account No." strong>
              <span className="wdd-mono">{row.account_number || "—"}</span>
              {!!row.account_number && (
                <button
                  className="wdd-copy"
                  title="Copy account number"
                  onClick={() => copy(row.account_number, "Account number")}
                >
                  <MdContentCopy size={13} />
                </button>
              )}
            </Row>
            <Row label="IFSC" strong>
              <span className="wdd-mono">{row.ifsc_code || "—"}</span>
              {!!row.ifsc_code && (
                <button
                  className="wdd-copy"
                  title="Copy IFSC"
                  onClick={() => copy(row.ifsc_code, "IFSC")}
                >
                  <MdContentCopy size={13} />
                </button>
              )}
            </Row>
            {!!row.upi_id && <Row label="UPI ID">{row.upi_id}</Row>}
          </section>

          <section className="wdd-section">
            <h6 className="wdd-section-title">Amount</h6>
            <Row label="Requested">₹{money(row.amount)}</Row>
            <Row label="Fee">
              {hasFee ? (
                <span className="wd-fee">
                  −₹{money(row.fee_amount)} ({Number(row.fee_percentage)}%)
                  <small className="wd-fee-note"> locked in player wallet</small>
                </span>
              ) : (
                "—"
              )}
            </Row>
            {/* Payout is both the bank transfer and the wallet movement - the
                fee never leaves the player, so nothing else is debited. */}
            <Row label="Payout" strong>
              ₹{money(row.payout_amount)}
            </Row>
            <Row label="Wallet movement">
              <span className="wdd-mono">
                −₹{money(row.payout_amount)} player / +₹
                {money(row.payout_amount)} {row.parent_name || "parent"}
              </span>
            </Row>
            <Row label="Reference">
              <span className="wdd-mono">{row.reference_id}</span>
            </Row>
          </section>

          <section className="wdd-section">
            <h6 className="wdd-section-title">Processing</h6>
            <Row label="Requested">{dt(row.created_at)}</Row>
            <Row label="Processed">{dt(row.processed_at)}</Row>
            <Row label="Processed By">{row.processed_by_name || "—"}</Row>
            {isApproved && (
              <Row label="Player Balance">
                {money(row.balance_before)} →{" "}
                <strong>{money(row.balance_after)}</strong>
              </Row>
            )}
          </section>
        </div>

        {!!row.remark && (
          <div className="wdd-note">
            <div className="wdd-note-label">Player note</div>
            <div>{row.remark}</div>
          </div>
        )}

        {!!row.admin_remark && (
          <div
            className={`wdd-note ${row.status === "REJECTED" ? "wdd-note-danger" : ""}`}
          >
            <div className="wdd-note-label">
              {row.status === "REJECTED" ? "Rejection reason" : "Admin remark"}
            </div>
            <div>{row.admin_remark}</div>
          </div>
        )}
      </Modal.Body>

      <div className="modal-footer border-0">
        <button type="button" className="btn btn-light px-4" onClick={onClose}>
          Close
        </button>
        {isPending && (
          <>
            <button
              type="button"
              className="btn btn-danger px-4"
              onClick={() => onAct(row, "reject")}
            >
              <MdCancel size={16} /> Reject
            </button>
            <button
              type="button"
              className="btn btn-success px-4"
              onClick={() => onAct(row, "approve")}
            >
              <MdCheckCircle size={16} /> Approve
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default WithdrawalDetailsModal;
